/**
 * Quick View Modal Custom Element
 * Handles async loading of product quick view content
 */
class QuickViewModal extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.currentProductHandle = null;
    this.loadedContent = null;
    this.swiperInstances = new Map();
  }

  connectedCallback() {
    // Create modal structure only when connected to DOM
    if (!this.overlay) {
      this.createModalStructure();
    }
    this.attachButtonListeners();
  }

  createModalStructure() {
    // Create modal structure using DOM methods instead of innerHTML
    const overlay = document.createElement("div");
    overlay.className = "quick-view-modal-overlay";

    const container = document.createElement("div");
    container.className = "quick-view-modal-container dagi-scrollbar";

    const loadingEl = document.createElement("div");
    loadingEl.className = "quick-view-modal-loading";
    loadingEl.innerHTML = `
      <div class="quick-view-loading-spinner">
        <div class="quick-view-loading-spinner-icon"></div>
        <span class="quick-view-loading-text">Yükleniyor...</span>
      </div>
    `;

    const contentEl = document.createElement("div");
    contentEl.className = "quick-view-modal-content";

    container.appendChild(loadingEl);
    container.appendChild(contentEl);
    overlay.appendChild(container);
    this.appendChild(overlay);

    this.overlay = overlay;
    this.container = container;
    this.loadingEl = loadingEl;
    this.contentEl = contentEl;

    // Close on overlay click
    this.overlay.addEventListener("click", (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Close on ESC key
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape" && this.isOpen) {
        this.close();
      }
    });
  }

  disconnectedCallback() {
    this.removeButtonListeners();
  }

  attachButtonListeners() {
    // Attach direct listeners to all quick buy buttons for better reliability
    document
      .querySelectorAll(".hollybuy-toggle, .quick-cart-button")
      .forEach((btn) => {
        if (!btn.dataset.quickViewAttached) {
          const handler = (e) => {
            if (
              btn.classList.contains("hollybuy-toggle") ||
              btn.classList.contains("quick-cart-button")
            ) {
              e.preventDefault();
              e.stopPropagation();
              e.stopImmediatePropagation();
              this.handleQuickBuyClick(e);
            }
          };
          btn.addEventListener("click", handler, true);
          btn.dataset.quickViewAttached = "true";
          btn._quickViewHandler = handler; // Store handler for removal
        }
      });
  }

  removeButtonListeners() {
    document.querySelectorAll("[data-quick-view-attached]").forEach((btn) => {
      if (btn._quickViewHandler) {
        btn.removeEventListener("click", btn._quickViewHandler, true);
        delete btn._quickViewHandler;
      }
      delete btn.dataset.quickViewAttached;
    });
  }

  handleQuickBuyClick(e) {
    // Check if clicked element is a quick buy button
    const quickBuyBtn = e.target.closest(
      ".hollybuy-toggle, .quick-cart-button"
    );
    if (!quickBuyBtn) return;

    // Always prevent default for quick buy buttons
    if (
      quickBuyBtn.classList.contains("hollybuy-toggle") ||
      quickBuyBtn.classList.contains("quick-cart-button")
    ) {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
    } else {
      // Not a quick buy button, let it navigate normally
      return;
    }

    // Get product handle from button attribute first
    let productHandle = quickBuyBtn.getAttribute("data-product-handle");

    // If no handle attribute, try to get from data-product-url
    if (!productHandle) {
      const productUrl = quickBuyBtn.getAttribute("data-product-url");
      if (productUrl) {
        try {
          const url = new URL(productUrl, window.location.origin);
          productHandle = url.pathname.split("/products/")[1]?.split("?")[0];
        } catch (err) {
          console.warn("Error parsing product URL:", err);
        }
      }
    }

    // If still no handle, try to get from product link in same block
    if (!productHandle) {
      const productLink = quickBuyBtn
        .closest(".product-block")
        ?.querySelector(".product-link");
      if (productLink && productLink.href) {
        try {
          const url = new URL(productLink.href, window.location.origin);
          productHandle = url.pathname.split("/products/")[1]?.split("?")[0];
        } catch (err) {
          console.warn("Error parsing product link URL:", err);
        }
      }
    }

    if (!productHandle) {
      console.warn("Quick buy button missing product handle", quickBuyBtn);
      const productUrl = quickBuyBtn.getAttribute("data-product-url");
      if (productUrl) {
        window.location.href = productUrl;
      }
      return;
    }

    this.open(productHandle, quickBuyBtn);
  }

  async open(productHandle, clickedButton = null) {
    if (this.isOpen && this.currentProductHandle === productHandle) {
      return; // Already open for this product
    }

    this.currentProductHandle = productHandle;

    // Show spinner on button
    if (clickedButton) {
      this.showButtonSpinner(clickedButton);
    }

    // Don't show modal yet - load content first
    this.overlay.classList.remove("is-open");
    this.contentEl.innerHTML = ""; // Clear previous content

    try {
      // Load content
      const content = await this.loadContent(productHandle);

      // Set content
      this.contentEl.innerHTML = content;

      // Initialize Alpine.js for dynamic content
      await this.initializeAlpine();

      // Wait a bit more for Alpine to fully initialize and set default variant
      await new Promise((resolve) => setTimeout(resolve, 150));

      // Initialize Swiper after a short delay to ensure DOM is ready
      await new Promise((resolve) => setTimeout(resolve, 100));
      this.initializeSwiper();

      // Execute any scripts in the loaded content
      this.executeScripts();

      // Now show modal
      this.isOpen = true;
      this.overlay.classList.add("is-open");
      this.contentEl.classList.add("is-visible");

      // Lock body scroll
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        document.body.style.paddingRight = "15px";
      }
      document.body.style.overflow = "hidden";

      // Hide button spinner
      if (clickedButton) {
        this.hideButtonSpinner(clickedButton);
      }
    } catch (error) {
      console.error("Error loading quick view:", error);

      // Hide button spinner
      if (clickedButton) {
        this.hideButtonSpinner(clickedButton);
      }

      // Show error in modal
      this.isOpen = true;
      this.overlay.classList.add("is-open");
      this.contentEl.classList.add("is-visible");
      this.contentEl.innerHTML = `
        <div style="display: flex; align-items: center; justify-content: center; width: 100%; height: 400px;">
          <div style="text-align: center;">
            <p style="color: #dc2626; margin-bottom: 1rem;">Ürün yüklenirken bir hata oluştu.</p>
            <button onclick="document.querySelector('quick-view-modal').close()" style="padding: 0.5rem 1rem; background-color: #000; color: #fff; border: none; border-radius: 3px; cursor: pointer;">Kapat</button>
          </div>
        </div>
      `;

      // Lock body scroll
      const isMobile = window.innerWidth < 768;
      if (!isMobile) {
        document.body.style.paddingRight = "15px";
      }
      document.body.style.overflow = "hidden";
    }
  }

  showButtonSpinner(button) {
    if (!button || button.dataset.spinnerAdded) return;

    // Store original content
    button.dataset.originalContent = button.innerHTML;
    button.dataset.originalDisabled =
      button.disabled || button.hasAttribute("disabled");
    button.dataset.originalBackground = button.style.backgroundColor || "";

    // Check if it's a mobile button (small circular button)
    const isMobileButton = button.classList.contains("quick-cart-button");

    // Disable button
    if (button.tagName === "A") {
      button.style.pointerEvents = "none";
      // Desktop: lightgray background instead of opacity
      if (!isMobileButton) {
        button.classList.add("quick-view-button-loading");
      }
    } else {
      button.disabled = true;
      // Desktop: lightgray background instead of opacity
      if (!isMobileButton) {
        button.classList.add("quick-view-button-loading");
      }
    }

    // Add spinner - different size for mobile vs desktop
    const spinner = document.createElement("span");

    if (isMobileButton) {
      // Small spinner for mobile circular button
      button.classList.add("quick-view-button-mobile-loading");

      // Hide original icon
      const icon = button.querySelector(
        ".quick-cart-icon, svg:not(.quick-view-button-spinner svg)"
      );
      if (icon) {
        icon.style.display = "none";
      }

      // Create and append spinner
      spinner.className =
        "quick-view-button-spinner quick-view-button-spinner-mobile";
      spinner.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="#000000" stroke-width="4" stroke-opacity="0.25"></circle>
          <path fill="#000000" fill-opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `;
      button.appendChild(spinner);
    } else {
      // Regular spinner for desktop button
      spinner.className =
        "quick-view-button-spinner quick-view-button-spinner-desktop";
      spinner.innerHTML = `
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" opacity="0.25"></circle>
          <path fill="currentColor" opacity="0.75" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
      `;
      // Insert spinner at the beginning
      button.insertBefore(spinner, button.firstChild);
    }

    button.dataset.spinnerAdded = "true";
  }

  hideButtonSpinner(button) {
    if (!button || !button.dataset.spinnerAdded) return;

    // Remove spinner
    const spinner = button.querySelector(".quick-view-button-spinner");
    if (spinner) {
      spinner.remove();
    }

    // Restore original icon for mobile button
    const isMobileButton = button.classList.contains("quick-cart-button");
    if (isMobileButton) {
      const icon = button.querySelector(
        ".quick-cart-icon, svg:not(.quick-view-button-spinner svg)"
      );
      if (icon) {
        icon.style.display = "";
      }
    }

    // Restore original state
    if (button.tagName === "A") {
      button.style.pointerEvents = "";
      button.classList.remove("quick-view-button-loading");
    } else {
      button.disabled = button.dataset.originalDisabled === "true";
      button.classList.remove("quick-view-button-loading");
    }

    // Remove mobile loading class
    button.classList.remove("quick-view-button-mobile-loading");

    delete button.dataset.spinnerAdded;
    delete button.dataset.originalContent;
    delete button.dataset.originalDisabled;
    delete button.dataset.originalBackground;
  }

  async loadContent(productHandle) {
    const url = `/products/${productHandle}?view=quick-view`;
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Failed to load product: ${response.status}`);
    }

    const html = await response.text();

    // Extract section content from response
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");

    let sectionContent = doc.querySelector(
      ".section-product-quick-view, .quick-view-content"
    );

    if (!sectionContent) {
      // Try to find by ID
      sectionContent = doc.querySelector("#shopify-section-product-quick-view");
    }

    if (!sectionContent) {
      // Last resort: return body content
      return doc.body ? doc.body.innerHTML : html;
    }

    return sectionContent.innerHTML;
  }

  async initializeAlpine() {
    // Wait for Alpine to be ready
    if (window.Alpine) {
      // Wait a bit for DOM to be ready
      await new Promise((resolve) => setTimeout(resolve, 50));

      // Force Alpine to re-evaluate the new content
      if (window.Alpine.initTree) {
        window.Alpine.initTree(this.contentEl);
      } else {
        // Fallback: trigger Alpine manually
        this.contentEl.querySelectorAll("[x-data]").forEach((el) => {
          if (window.Alpine && !window.Alpine.$data(el)) {
            window.Alpine.initTree(el);
          }
        });
      }
    }
  }

  initializeSwiper() {
    if (typeof Swiper === "undefined") {
      console.warn("Swiper not loaded");
      return;
    }

    // Find all swiper containers in the content
    const swiperContainers = this.contentEl.querySelectorAll(
      ".unique-swiper-container"
    );

    swiperContainers.forEach((container) => {
      const productId = container.className.match(
        /unique-swiper-container-(\d+)/
      )?.[1];
      if (!productId) return;

      // Destroy existing instance if any
      if (this.swiperInstances.has(productId)) {
        this.swiperInstances.get(productId).destroy(true, true);
      }

      const paginationEl = container.querySelector(".unique-swiper-pagination");
      if (!paginationEl) return;

      // Ensure swiper-wrapper has proper dimensions
      const wrapper = container.querySelector(".swiper-wrapper");
      if (wrapper) {
        wrapper.classList.add("quick-view-swiper-wrapper");
      }

      // Ensure slides have proper dimensions
      const slides = container.querySelectorAll(".swiper-slide");
      slides.forEach((slide) => {
        slide.classList.add("quick-view-swiper-slide");
      });

      // Ensure images have proper dimensions
      const images = container.querySelectorAll("img");
      images.forEach((img) => {
        img.classList.add("quick-view-swiper-image");
      });

      const swiper = new Swiper(container, {
        slidesPerView: 1,
        spaceBetween: 10,
        autoHeight: false, // Disable autoHeight to prevent size issues
        pagination: {
          el: paginationEl,
          clickable: true,
          type: "bullets",
          dynamicBullets: false,
        },
        simulateTouch: true,
        observer: true,
        observeParents: true,
        watchOverflow: true,
      });

      this.swiperInstances.set(productId, swiper);

      // Update Swiper after images load to recalculate dimensions
      let loadedImages = 0;
      const totalImages = images.length;

      if (totalImages > 0) {
        images.forEach((img) => {
          if (img.complete) {
            loadedImages++;
            if (loadedImages === totalImages) {
              setTimeout(() => {
                swiper.update();
                swiper.updateSize();
                swiper.updateSlides();
              }, 100);
            }
          } else {
            img.addEventListener("load", () => {
              loadedImages++;
              if (loadedImages === totalImages) {
                setTimeout(() => {
                  swiper.update();
                  swiper.updateSize();
                  swiper.updateSlides();
                }, 100);
              }
            });
          }
        });
      } else {
        setTimeout(() => {
          swiper.update();
          swiper.updateSize();
          swiper.updateSlides();
        }, 100);
      }
    });
  }

  executeScripts() {
    // Find and execute all script tags in the loaded content
    const scripts = this.contentEl.querySelectorAll("script");
    scripts.forEach((oldScript) => {
      const newScript = document.createElement("script");

      // Copy attributes
      Array.from(oldScript.attributes).forEach((attr) => {
        newScript.setAttribute(attr.name, attr.value);
      });

      // Copy content
      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      } else if (oldScript.innerHTML) {
        newScript.innerHTML = oldScript.innerHTML;
      }

      // Replace old script with new one
      oldScript.parentNode.replaceChild(newScript, oldScript);
    });
  }

  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.currentProductHandle = null;

    // Destroy Swiper instances
    this.swiperInstances.forEach((swiper) => {
      swiper.destroy(true, true);
    });
    this.swiperInstances.clear();

    // Hide modal
    this.overlay.classList.remove("is-open");

    // Unlock body scroll
    const isMobile = window.innerWidth < 768;
    if (!isMobile) {
      document.body.style.paddingRight = "0";
    }
    document.body.style.overflow = "visible";

    // Clear content after animation
    setTimeout(() => {
      this.contentEl.innerHTML = "";
      this.contentEl.classList.remove("is-visible");
    }, 300);

    // Remove any remaining button spinners (safety cleanup)
    document.querySelectorAll("[data-spinner-added]").forEach((btn) => {
      this.hideButtonSpinner(btn);
    });
  }
}

// Register custom element
if (!customElements.get("quick-view-modal")) {
  customElements.define("quick-view-modal", QuickViewModal);
}

// Create global instance when DOM is ready
function initQuickViewModal() {
  if (!window.quickViewModal) {
    window.quickViewModal = document.createElement("quick-view-modal");
    document.body.appendChild(window.quickViewModal);

    // Re-attach listeners when new content is added (for dynamic content)
    const observer = new MutationObserver(() => {
      if (
        window.quickViewModal &&
        window.quickViewModal.attachButtonListeners
      ) {
        window.quickViewModal.attachButtonListeners();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initQuickViewModal);
} else {
  initQuickViewModal();
}
